import { useState, useEffect, useRef, useCallback, type CSSProperties } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";

const NAV_ITEMS = [
  { label: "About", to: "/about", type: "link" },
  { label: "How It Works", to: "/how-it-works", type: "link" },
  { label: "Artists", to: "/#artists", type: "anchor" },
  { label: "Portfolio", to: "/#portfolio", type: "anchor" },
];

const CTA = { label: "Commission Now", to: "/commission", type: "link" };

function NavItem({
  item,
  onClick,
  onPointerEnter,
  className,
  style,
}: {
  item: typeof NAV_ITEMS[0];
  onClick?: () => void;
  onPointerEnter?: (e: React.PointerEvent<HTMLElement>) => void;
  className?: string;
  style?: CSSProperties;
}) {
  const baseClasses = "focus-ring relative z-10 rounded-full transition-colors duration-200";
  const activeClasses = "text-[var(--color-primary)]";
  const inactiveClasses = "text-[var(--color-text-muted)] hover:text-[var(--color-text)]";

  if (item.type === "anchor") {
    return (
      <a
        href={item.to}
        onClick={onClick}
        onPointerEnter={onPointerEnter}
        className={cn(baseClasses, inactiveClasses, className)}
        style={style}
      >
        {item.label}
      </a>
    );
  }

  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      onPointerEnter={onPointerEnter}
      className={({ isActive }) =>
        cn(baseClasses, isActive ? activeClasses : inactiveClasses, className)
      }
      style={style}
    >
      {item.label}
    </NavLink>
  );
}

/** Tracks a pill that glides under whichever nav item the pointer is over,
 *  and rests on the current page's item (or fades out) when it leaves. */
function useNavIndicator() {
  const navRef = useRef<HTMLElement>(null);
  const [style, setStyle] = useState<CSSProperties>({});
  const { pathname } = useLocation();

  const moveTo = useCallback((el: HTMLElement | null) => {
    if (!el || !navRef.current) {
      setStyle((s) => ({ ...s, ["--o" as string]: 0 }));
      return;
    }
    const nav = navRef.current.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    setStyle({
      ["--x" as string]: `${r.left - nav.left}px`,
      ["--w" as string]: `${r.width}px`,
      ["--o" as string]: 1,
    });
  }, []);

  const rest = useCallback(() => {
    moveTo(navRef.current?.querySelector<HTMLElement>('a[aria-current="page"]') ?? null);
  }, [moveTo]);

  // Re-anchor on route change (after NavLink updates aria-current).
  useEffect(() => {
    const id = requestAnimationFrame(rest);
    return () => cancelAnimationFrame(id);
  }, [pathname, rest]);

  return { navRef, style, moveTo, rest };
}

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { navRef, style: indicatorStyle, moveTo, rest } = useNavIndicator();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Mobile sheet: lock page scroll and close on Escape while it's open.
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setIsMobileMenuOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [isMobileMenuOpen]);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  // Fully hidden at the top of the page; slides in with the background once scrolled.
  // Stays visible while the mobile menu is open so it can't vanish mid-interaction.
  const isVisible = isScrolled || isMobileMenuOpen;

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-[transform,opacity,visibility] duration-500 ease-[var(--ease-out-expo)]",
        isVisible ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0 invisible"
      )}
    >
      <div
        className={cn(
          "relative z-10 border-b transition-colors duration-500",
          isMobileMenuOpen
            ? "bg-[var(--color-bg)] border-transparent"
            : "bg-[var(--color-bg)]/70 backdrop-blur-xl backdrop-saturate-150 border-white/[0.06]"
        )}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" onClick={closeMobileMenu} className="focus-ring flex items-center rounded-lg">
              <img
                src="/static/images/branding/logo anterior.png"
                alt="StudioComm"
                className="h-16 w-auto transition-transform duration-500 ease-[var(--ease-spring)] hover:scale-[1.04]"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav
              ref={navRef}
              onPointerLeave={rest}
              className="relative hidden md:flex items-center gap-1"
            >
              <span aria-hidden className="nav-indicator" style={indicatorStyle} />
              {NAV_ITEMS.map((item) => (
                <NavItem
                  key={item.label}
                  item={item}
                  className="px-3.5 py-2 text-sm font-medium"
                  onPointerEnter={(e) => moveTo(e.currentTarget)}
                />
              ))}
              <Link
                to={CTA.to}
                onPointerEnter={() => moveTo(null)}
                className="pressable cta-glow focus-ring ml-3 inline-flex items-center justify-center h-9 px-5 rounded-full bg-[var(--color-primary)] text-[var(--color-bg)] text-sm font-medium hover:bg-[var(--color-primary-light)]"
              >
                {CTA.label}
              </Link>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="focus-ring md:hidden relative h-10 w-10 -mr-2 rounded-full text-[var(--color-text)] pressable"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
            >
              <span className="menu-icon absolute inset-x-[11px] inset-y-[12px]" data-open={isMobileMenuOpen}>
                <span />
                <span />
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation — full-height sheet that drops from under the bar */}
      <div
        id="mobile-menu"
        data-open={isMobileMenuOpen}
        inert={!isMobileMenuOpen}
        className={cn(
          "md:hidden absolute inset-x-0 top-16 h-[calc(100dvh-4rem)] overflow-y-auto bg-[var(--color-bg)]/95 backdrop-blur-2xl",
          "transition-[opacity,clip-path] duration-500 ease-[var(--ease-out-expo)]",
          isMobileMenuOpen
            ? "opacity-100 [clip-path:inset(0_0_0_0)]"
            : "opacity-0 pointer-events-none [clip-path:inset(0_0_100%_0)]"
        )}
      >
        <nav className="max-w-7xl mx-auto px-6 pt-6 pb-10 flex flex-col">
          {NAV_ITEMS.map((item, i) => (
            <div key={item.label} className="border-b border-white/[0.06]">
              <NavItem
                item={item}
                onClick={closeMobileMenu}
                className="mobile-menu-item block py-4 text-2xl font-medium tracking-tight"
                style={{ ["--i" as string]: i }}
              />
            </div>
          ))}
          <div className="pt-8">
            <Link
              to={CTA.to}
              onClick={closeMobileMenu}
              className="mobile-menu-item pressable focus-ring flex items-center justify-center h-12 px-6 rounded-full bg-[var(--color-primary)] text-[var(--color-bg)] text-base font-semibold"
              style={{ ["--i" as string]: NAV_ITEMS.length }}
            >
              {CTA.label}
            </Link>
          </div>
        </nav>
      </div>
    </header>
  );
}
